import { Button, Checkbox, Input, Popconfirm, Row, Select, Space, Table, Tabs } from "antd";
import { SettingOutlined, FilterOutlined, SearchOutlined, FileExcelOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import { BaseComponent } from "../../components/infrastructure/baseComponent";
import { aggregateFunctions, IDamageCaseReportArgs, IDamageCaseReportColumn, IDamageCaseReportProps, IDamageCaseReportStates, IReportExecuteData, IReportTemplate, IReportTemplateData } from "./interfaces";
import CustomSpinner from "../../components/infrastructure/customSpinner";
import { AdvancedSearch, IAdvancedSearch } from "../../components/advancedSearch";
import { Brand, Center, DamageType, EquipmentModel, Policyholder, Province, User } from "../../home/components";
import { damageCaseStatusOptions, evaluationMethodsOptions, evaluationStatusOptions } from "../../baseInfo/damageCase/interfaces";
import { personalityTypesOptions } from "../../baseInfo/center/interfaces";
import { documentCheckStatesOptions } from "../../evaluation/damageCaseEvaluator/documentCheck";
import { contactStatusOptions } from "../../evaluation/damageCaseEvaluator/makingContact";
import { policyholderResponseTypesOptions } from "../../evaluation/damageCaseEvaluator/policyholderConfirmation";
import { City } from "../../baseInfo/city";
import { IReportColumn } from "../interfaces";
import { AndFilter, ComparisonFilter, enmConditionalOperators, IFilter } from "../../components/infrastructure/filtering";
import { Core } from "../../components/infrastructure/core";
import { enmEntityState, enmOrderingType, ICallOptions, IFetchingInfo, IFetchingResult, IOrdering, IPaging, IResult, IResultSet } from "../../components/infrastructure/models";
import { ResizableTitle } from "../../components/entityManager/resizabletitle";
import dayjs from 'dayjs';
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Excel } from "antd-table-saveas-excel";
import { enmControlType, enmPropertyType } from "../../components/entityManager/interfaces";
import { EquipmentItem } from "../../baseInfo/equipment/equipmentItem";
import React from "react";
import { ColumnType } from "antd/lib/table";

const { TabPane } = Tabs;
const { Option } = Select;

export class DamageCaseReport extends BaseComponent<IDamageCaseReportProps, IDamageCaseReportStates>{
    private _advancedSearch?: IAdvancedSearch | null;
    private _gridContainer?: HTMLElement | null;
    title: string = 'گزارش خسارت';
    paging?: IPaging;
    ordering?: IOrdering;
    currentRecord?: any;
    reportColumns: IReportColumn[];
    displayColumnsData: IDamageCaseReportColumn[];
    aggregateTitles: { value?: string; title: string; }[];
    dragColumn?: string;
    constructor(props: IDamageCaseReportProps) {
        super(props)
        this.paging = this.paging ?? { PageNo: 1, PageSize: 10 };
        this.reportColumns = this.getReportColumns();
        this.displayColumnsData = this.reportColumns.map(f => { return { key: f.displayColumn ?? f.name, name: f.name, title: f.title, orderNo: 1000 }; });
        const defaultDisplayColumns = this.reportColumns.filter(c => c.isDefaultDisplay === true).map(c => c.displayColumn ?? c.name);
        this.state = {
            isAuthorized: true,
            loading: true,
            windowSize: 2,
            defaultGroupingColumns: [],
            defaultDisplayColumns: defaultDisplayColumns,
            columns: [],
            groupingColumns: [],
            displayColumns: defaultDisplayColumns,
            resultColumns: [],
            totalResultCount: 0,
            access: [],
            defaultSearchParams: []
        };

        this.aggregateTitles = [{ title: "...", value: undefined }];
        for (let key in aggregateFunctions) {
            this.aggregateTitles.push({ value: aggregateFunctions[key].value, title: aggregateFunctions[key].title });
        }
    }
    getReportColumns(): IReportColumn[] {
        return [
            {
                name: "DamageCaseTicketNo",
                title: "شماره تیکت",
                propertyType: enmPropertyType.number,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseCode",
                title: "شماره پرونده خسارت",
                propertyType: enmPropertyType.number,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseEmergencyCallNumber",
                title: "شماره تماس اضطراری"
            },
            {
                name: "DamageCaseReason",
                title: "دلیل خسارت",
                propertyType: enmPropertyType.string,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseTypeId",
                title: "نوع خسارت",
                displayColumn: "DamageCaseTypeName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: DamageType,
                    lookupProperty: "DamageCaseType",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseConfirmPossessionTransition",
                title: "تایید انتقال مالکیت؟",
                controlType: enmControlType.checkbox,
                propertyType: enmPropertyType.boolean
            },
            {
                name: "DamageCaseDate",
                title: "تاریخ خسارت",
                controlType: enmControlType.date,
                propertyType: enmPropertyType.date,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseProvinceId",
                title: "استان محل حضور دستگاه",
                displayColumn: "DamageCaseProvinceName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Province,
                    lookupProperty: "DamageCaseProvince",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true
            },
            {
                name: "DamageCaseCityId",
                title: "شهر محل حضور دستگاه",
                displayColumn: "DamageCaseCityName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: City,
                    lookupProperty: "DamageCaseCity",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true
            },
            {
                name: "DamageCaseAddress",
                title: "آدرس محل حضور دستگاه"
            },
            {
                name: "DamageCaseDamageProvinceId",
                title: "استان محل خسارت",
                displayColumn: "DamageCaseDamageProvinceName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Province,
                    lookupProperty: "DamageCaseDamageProvince",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true
            },
            {
                name: "DamageCaseDamageCityId",
                title: "شهر محل خسارت",
                displayColumn: "DamageCaseDamageCityName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: City,
                    lookupProperty: "DamageCaseDamageCity",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true
            },
            {
                name: "DamageCaseDamageAddress",
                title: "آدرس محل خسارت"
            },
            {
                name: "DamageCaseStatus",
                title: "وضعیت پرونده",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: damageCaseStatusOptions
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseEvaluationStatus",
                title: "وضعیت ارزیابی",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: evaluationStatusOptions
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "DamageCaseCreateDateTime",
                title: "تاریخ و زمان ایجاد پرونده خسارت",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "DamageCaseCreatorId",
                title: "کاربر ایجادکننده خسارت",
                displayColumn: "DamageCaseCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "DamageCaseCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "EquipmentId",
                title: "تجهیز",
                displayColumn: "EquipmentName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: EquipmentItem,
                    lookupProperty: "EquipmentItem",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "BrandId",
                title: "برند",
                displayColumn: "BrandName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Brand,
                    lookupProperty: "Brand",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "EquipmentModelId",
                title: "مدل تجهیز",
                displayColumn: "EquipmentModelName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: EquipmentModel,
                    lookupProperty: "EquipmentModel",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "EquipmentSerialNo",
                title: "سریال مدل تجهیز(IMEI)",
                propertyType: enmPropertyType.string
            },
            {
                name: "PlolicySerialNo",
                title: "سریال بیمه نامه",
                propertyType: enmPropertyType.string,
                isGroupable: true
            },
            {
                name: "PolicyPlanName",
                title: "نام طرح",
                propertyType: enmPropertyType.string,
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "PolicyStartDateTime",
                title: "تاریخ شروع بیمه نامه",
                controlType: enmControlType.date,
                propertyType: enmPropertyType.date
            },
            {
                name: "PolicyEndDateTime",
                title: "تاریخ شروع بیمه نامه",
                controlType: enmControlType.date,
                propertyType: enmPropertyType.date
            },
            {
                name: "PolicyInsuredValue",
                title: "سرمایه بیمه نامه",
                propertyType: enmPropertyType.number
            },
            {
                name: "PolicyholderNationalCode",
                title: "کد ملی بیمه گزار/مشتری",
                propertyType: enmPropertyType.string,
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "PolicyholderId",
                title: "نام بیمه گزار/مشتری",
                displayColumn: "PolicyholderFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Policyholder,
                    lookupProperty: "Policyholder",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "PolicyholderPersonality",
                title: "نوع بیمه گزار/مشتری",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: personalityTypesOptions
                },
                isGroupable: true
            },
            {
                name: "PolicyholderPhone",
                title: "شماره تماس بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "PolicyholderPostalCode",
                title: "کد پستی بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "PolicyholderProvinceId",
                title: "استان بیمه گزار/مشتری",
                displayColumn: "PolicyholderProvinceName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Province,
                    lookupProperty: "Province",
                    lookupDisplayFields: ["Name"]
                },
                isGroupable: true
            },
            {
                name: "PolicyholderCityId",
                title: "شهر بیمه گزار/مشتری",
                displayColumn: "PolicyholderCityName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: City,
                    lookupProperty: "City",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "PolicyholderAddress",
                title: "آدرس بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "PolicyholderBirthDate",
                title: "تاریخ تولد بیمه گزار/مشتری",
                controlType: enmControlType.date,
                propertyType: enmPropertyType.date,
            },
            {
                name: "PolicyholderFatherName",
                title: "نام پدر بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "AllocationDateTime",
                title: "تاریخ و زمان تخصیص ارزیاب",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "AllocatorId",
                title: "تخصیص دهنده ارزیاب",
                displayColumn: "AllocatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "Allocator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "EvaluatorId",
                title: "ارزیاب",
                displayColumn: "EvaluatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: Center,
                    lookupProperty: "Evaluator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true,
                isDefaultDisplay: true
            },
            {
                name: "DocumentCheckDateTime",
                title: "تاریخ و زمان بررسی مدارک",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "DocumentCheckCreatorId",
                title: "کاربر بررسی کننده مدارک",
                displayColumn: "DocumentCheckCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "DocumentCheckCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "DocumentCheckDescription",
                title: "توضیحات بررسی کننده مدارک",
                propertyType: enmPropertyType.string
            },
            {
                name: "DocumentCheckState",
                title: "نتیجه بررسی مدارک",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: documentCheckStatesOptions
                },
                isGroupable: true
            },
            {
                name: "ContactSettingDateTime",
                title: "تاریخ و زمان تنظیم تماس",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "ContactSettingCreatorId",
                title: "کاربر تنظیم کننده تماس",
                displayColumn: "ContactSettingCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "ContactSettingCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "ContactSettingDescription",
                title: "توضیحات تنظیم کننده تماس",
                propertyType: enmPropertyType.string
            },
            {
                name: "ContactSettingContactDate",
                title: "تاریخ و زمان برقراری ارزیابی",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "ContactSettingConferenceUrl",
                title: "آدرس تماس تصویری",
                propertyType: enmPropertyType.string
            },
            {
                name: "EvaluationMethod",
                title: "روش تماس ارزیابی",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: evaluationMethodsOptions
                },
                isGroupable: true
            },
            {
                name: "MakingContactDateTime",
                title: "تاریخ و زمان ثبت برقراری تماس",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "MakingContactCreatorId",
                title: "کاربر برقرار کننده تماس",
                displayColumn: "MakingContactCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "MakingContactCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "MakingContactStatus",
                title: "نتیجه برقراری تماس",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: contactStatusOptions
                }
            },
            {
                name: "MakingContactDescription",
                title: "توضیحات برقرار کننده تماس"
            },
            {
                name: "DamageEstimationDateTime",
                title: "تاریخ و زمان ثبت برآورد خسارت",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "DamageEstimationCreatorId",
                title: "کاربر برآورد خسارت",
                displayColumn: "DamageEstimationCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "DamageEstimationCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "DamageEstimationEquipmentPrice",
                title: "قیمت روز دستگاه",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number
            },
            {
                name: "DamageEstimationFranchise1Description",
                title: "توضیحات فرانشیز اول",
                propertyType: enmPropertyType.string
            },
            {
                name: "DamageEstimationFranchise1EstimatedAmount",
                title: "مبلغ برآورد شده فرانشیز اول",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number
            },
            {
                name: "DamageEstimationFranchise1Amount",
                title: "مبلغ فرانشیز اول",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number
            },
            {
                name: "DamageEstimationFranchise2Description",
                title: "توضیحات فرانشیز دوم",
                propertyType: enmPropertyType.string
            },
            {
                name: "DamageEstimationFranchise2EstimatedAmount",
                title: "مبلغ برآورد شده فرانشیز دوم",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number
            },
            {
                name: "DamageEstimationFranchise2Amount",
                title: "مبلغ فرانشیز دوم",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number
            },
            {
                name: "DamageEstimationPayableAmount",
                title: "مبلغ قابل پرداخت",
                controlType: enmControlType.priceinput,
                propertyType: enmPropertyType.number,
                isDefaultDisplay: true
            },
            {
                name: "PolicyholderConfirmationDateTime",
                title: "تاریخ و زمان ثبت پاسخ بیمه گزار/مشتری",
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            },
            {
                name: "PolicyholderConfirmationCreatorId",
                title: "کاربر ثبت کننده پاسخ بیمه گزار/مشتری",
                displayColumn: "PolicyholderConfirmationCreatorFullName",
                controlType: enmControlType.lookup,
                propertyType: enmPropertyType.number,
                lookup: {
                    parent: this,
                    lookupComponent: User,
                    lookupProperty: "PolicyholderConfirmationCreator",
                    lookupDisplayFields: ["FullName"]
                },
                isGroupable: true
            },
            {
                name: "PolicyholderConfirmationResponseType",
                title: "پاسخ بیمه گزار/مشتری",
                controlType: enmControlType.combobox,
                propertyType: enmPropertyType.number,
                combobox: {
                    options: policyholderResponseTypesOptions
                },
                isDefaultDisplay: true
            },
            {
                name: "PolicyholderConfirmationObjectionDescription",
                title: "شرح اعتراض بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "PolicyholderConfirmationShebaNo",
                title: "شماره شبای بیمه گزار/مشتری",
                propertyType: enmPropertyType.string
            },
            {
                name: "ReceiveDepreciatedDateTime",
                title: 'تاریخ و زمان تأیید "دریافت داغی" یا "دریافت انتقال مالکیت"',
                controlType: enmControlType.datetime,
                propertyType: enmPropertyType.datetime
            }
        ];
    }
    componentDidMount() {
        super.componentDidMount();
        this.loadTemplate();
    }
    setDefaults = () => {
        this.setState({
            columns: [],
            resultColumns: [],
            data: undefined,
            groupingColumns: this.state.defaultGroupingColumns,
            displayColumns: this.state.defaultDisplayColumns,
            defaultFilter: this.state.defaultFilter
        });
        this._advancedSearch?.setDefaultStructure();
    }
    loadTemplate = async () => {
        const { params } = this.context;
        let opt: ICallOptions = {
            url: 'reporttemplate/get',
            data: { templateId: params.templateid ?? null },
            onSuccess: (result: IResult) => {
                const response = result as IResultSet<IReportTemplateData>;
                if (response.Data) {
                    let { Template, Access, DefaultSearchParams } = response.Data;
                    if (!Template) {
                        this.setState({
                            access: Access ?? [],
                            defaultSearchParams: DefaultSearchParams
                        });
                        return;
                    }

                    this.title = Template.Title;

                    const displayColumns = !Template.DisplayColumns || Template.DisplayColumns === "" ? [] : Template.DisplayColumns.split(',');
                    const groupingColumns = !Template.GroupingColumns || Template.GroupingColumns === "" ? [] : Template.GroupingColumns.split(',');
                    const defaultDisplayColumns = displayColumns.map(c => c.split('|')[0]);

                    displayColumns.forEach(key => {
                        const parts = key.split('|');
                        let item = this.displayColumnsData.find(i => i.key === parts[0]);
                        if (item && parts[1])
                            item.aggregateFunc = parts[1];
                    });

                    const defaultFilter = Template.DefaultFilter ? JSON.parse(Template.DefaultFilter) : undefined;

                    this.setState({
                        defaultDisplayColumns: defaultDisplayColumns,
                        defaultGroupingColumns: groupingColumns,
                        defaultFilter: defaultFilter,
                        showCountColumn: Template.ShowCountColumn,
                        columns: [],
                        resultColumns: [],
                        data: undefined,
                        groupingColumns: groupingColumns,
                        displayColumns: defaultDisplayColumns,
                        access: Access,
                        currentTemplate: Template,
                        templateTitle: Template.Title,
                        defaultSearchParams: DefaultSearchParams
                    });
                    this._advancedSearch?.setDefaultStructure();
                }
            }
        };
        await this.callApi(opt);
    }
    saveTemplate = async () => {
        if (!this.state.templateTitle || this.state.templateTitle === "") {
            Core.warningNotification("لطفا عنوان الگو را وارد نمایید");
            return;
        }
        const displayColumns = this.displayColumnsData.filter(c => this.state.displayColumns.indexOf(c.key) > -1).map(c => c.key + (!!c.aggregateFunc ? '|' + c.aggregateFunc : ''));
        const groupingColumns: string[] = this.state.groupingColumns;
        const defaultFilter = this._advancedSearch?.getFilterStructure();
        const reportTemplate = {
            RowGuid: this.state.currentTemplate?.RowGuid,
            RowVrsn: this.state.currentTemplate?.RowVrsn,
            Name: 'TestReportTemplate',
            Title: this.state.templateTitle,
            GroupingColumns: groupingColumns.join(','),
            DisplayColumns: displayColumns.join(','),
            ShowCountColumn: this.state.showCountColumn === true,
            DefaultFilter: defaultFilter ? JSON.stringify(defaultFilter) : null,
            State: this.state.currentTemplate ? enmEntityState.Modified : enmEntityState.Added,
            ModifiedProperties: ["Name", "Title", "GroupingColumns", "DisplayColumns", "DefaultFilter", "ShowCountColumn"]
        };
        let opt: ICallOptions = {
            url: 'reporttemplate/save',
            data: {
                reportTemplate
            },
            onSuccess: (result: IResultSet<any>) => {
                let response = result.Data as IReportTemplate;
                if (response && result.ValidationState.Successful) {
                    Core.successNotification('تغییرات با موفقیت ذخیره شد');
                    this.setState({ currentTemplate: response });
                }
            }
        };
        await this.callApi(opt);
    }
    deleteTemplate = async () => {
        if (!this.state.currentTemplate)
            return;

        let opt: ICallOptions = {
            url: 'reporttemplate/delete',
            data: {
                template: this.state.currentTemplate
            },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful) {
                    Core.successNotification('الگو با موفقیت حذف شد');
                    this.setState({ currentTemplate: null });
                }
            }
        };
        await this.callApi(opt);
    }
    submit = (args: IDamageCaseReportArgs) => {
        if (!this.state.displayColumns.length) {
            Core.warningNotification('در بخش تنظیمات گزارش، حداقل یک ستون جهت نمایش انتخاب نمایید');
            return;
        }

        this._advancedSearch?.submit(args);
    }
    onSubmit = async (args: IDamageCaseReportArgs, filter?: IFilter | null) => {
        const displayColumns = this.displayColumnsData.filter(c => this.state.displayColumns.indexOf(c.key) > -1).map(c => c.key + (!!c.aggregateFunc ? '|' + c.aggregateFunc : ''));
        const groupingColumns: string[] = this.state.groupingColumns.map(c => this.displayColumnsData.find(i => i.name === c.split('|')[0])!.key);
        const fetchingInfo: IFetchingInfo = {
            Filter: filter ?? undefined,
            Orderings: !!this.ordering ? [this.ordering] : [],
            Paging: args.paging ?? undefined
        };

        if (this.state.showCountColumn && !!this.state.groupingColumns.length) {
            displayColumns.push('_col_count_|COUNT(1)');
        }

        await this.execute(args, fetchingInfo, displayColumns, groupingColumns);
    }
    renderColumn = (record: any, args: IDamageCaseReportArgs, originalCol: IReportColumn, key: string, width?: number | string) => {
        width = this.state.resultColumns?.find((col: ColumnType<any>) => col.dataIndex?.toString() === key)?.width ?? width ?? 200;
        let result = record[key];

        if (originalCol.controlType === enmControlType.combobox) {
            result = originalCol?.combobox?.options?.find(f => f.value === record[key])?.title;
        }
        if (originalCol.controlType === enmControlType.checkbox && args.source === "search") {
            result = <Checkbox checked={record[key]} disabled />;
        }
        if (originalCol.controlType === enmControlType.priceinput) {
            result = record[key] === null ? '' : `${record[key]}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        if (originalCol.controlType === enmControlType.date) {
            result = !!record[key] && dayjs(record[key]).calendar('jalali').locale('fa').format("YYYY/MM/DD");
        }
        if (originalCol.controlType === enmControlType.datetime) {
            result = !!record[key] && dayjs(record[key]).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm");
        }

        if (args.source === "excel")
            return result;

        return (
            <div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {result === null ? '' : result}
            </div>
        );
    }
    getColumns = (args: IDamageCaseReportArgs, displayColumns: string[], groupingColumns: string[], dblClicked: boolean = false) => {
        let resultColumns: any[] = [];
        this.displayColumnsData.filter(c => this.state.displayColumns.indexOf(c.key) > -1)
            .sort((a, b) => this.state.displayColumns.indexOf(a.key) - this.state.displayColumns.indexOf(b.key))
            .forEach(c => {
                let col: ColumnType<any> = {
                    key: c.key,
                    dataIndex: c.key,
                    title: groupingColumns.length ? c.aggregateTitle ?? c.title : c.title,
                    sorter: true,
                    width: 200
                }
                let originalCol = this.reportColumns.find(r => r.name === c.name);
                if (originalCol) {
                    const parts = displayColumns.find(item => item.split('|')[0] === col.dataIndex)?.split('|')!;
                    if (!groupingColumns.length || (groupingColumns.length && parts.length === 2) || groupingColumns.find(item => item === parts[0])) {
                        col.render = (_, record) => this.renderColumn(record, args, originalCol!, c.key, col.width);
                        resultColumns.push(col);
                    }
                }
            });

        if (this.state.showCountColumn && !!groupingColumns.length && !dblClicked) {
            let colCount = {
                key: '_col_count_', dataIndex: '_col_count_', title: 'تعداد', width: 200, sorter: true,
                render: (_: string, record: any) => {
                    return `${record['_col_count_']}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                }
            };
            resultColumns.push(colCount);
        }
        return resultColumns;
    }
    execute = async (args: IDamageCaseReportArgs, fetchingInfo: IFetchingInfo, displayColumns: string[], groupingColumns: string[], dblClicked: boolean = false) => {
        this.setState({ data: args.source === "search" ? [] : null });

        const currentExecuteData: IReportExecuteData = {
            fetchingInfo,
            groupingColumns,
            displayColumns
        };

        await this.callApi({
            url: "damagecasereport/execute",
            data: currentExecuteData,
            onSuccess: (result: IResult | IResultSet<any> | IFetchingResult<any>) => {
                let response = result as IFetchingResult<any>;
                if (!response.Data)
                    return;

                let resultColumns: any[] = this.getColumns(args, displayColumns, groupingColumns, dblClicked);
                if (args.source === 'search') {
                    this.setState({ data: response.Data, resultColumns, totalResultCount: response.TotalResultCount, currentExecuteData: dblClicked ? this.state.currentExecuteData : currentExecuteData, dblClicked, detailsFetchingInfo: dblClicked ? fetchingInfo : null });
                }
                else if (args.source === 'excel') {
                    const excel = new Excel();
                    excel
                        .addSheet(this.title)
                        .addColumns(resultColumns)
                        .addDataSource(response.Data, {
                            str2Percent: true
                        })
                        .saveAs(this.title + ".xlsx");

                    this.setState({ data: null });
                }
            }
        });
    }
    search = () => {
        this.ordering = undefined;
        this.paging = {
            PageNo: 1,
            PageSize: this.paging!.PageSize
        };
        this.submit({ source: 'search', paging: this.paging });
    }
    excel = async () => {
        if (!this.state.dblClicked) {
            this.ordering = undefined;
            this.submit({ source: 'excel' });
        }
        else {
            if (this.state.currentExecuteData && this.state.detailsFetchingInfo) {
                const { displayColumns } = this.state.currentExecuteData;
                const detailsFetchingInfo: IFetchingInfo = {
                    Filter: this.state.detailsFetchingInfo.Filter,
                    Orderings: this.state.detailsFetchingInfo.Orderings
                }
                await this.execute({ source: 'excel' }, detailsFetchingInfo, displayColumns, []);
            }
        }
    }
    onSelectedDisplayColumnsChanged = (selectedRowKeys: React.Key[]) => {
        this.setState({ displayColumns: selectedRowKeys.map(c => c as string) });
    }
    handleResize = (index: number) => (_: any, { size }: any) => {
        this.setState(({ resultColumns }) => {
            const nextColumns = [...resultColumns];
            nextColumns[index] = {
                ...nextColumns[index],
                width: size.width,
            };
            return { resultColumns: nextColumns };
        });
    }
    onRowDoubleClick = async (record: any) => {
        if (this.state.currentExecuteData) {
            this.currentRecord = record;
            const { fetchingInfo, groupingColumns, displayColumns } = this.state.currentExecuteData;
            if (!this.state.dblClicked && groupingColumns.length) {
                const filters: ComparisonFilter[] = [];
                groupingColumns.forEach(f => {
                    filters.push(new ComparisonFilter(f, enmConditionalOperators.Equal, this.currentRecord[f]));
                });
                let currentFetchingInfo: IFetchingInfo = {
                    Paging: { PageNo: 1, PageSize: this.paging!.PageSize },
                    Orderings: []
                };
                if (fetchingInfo.Filter)
                    currentFetchingInfo.Filter = new AndFilter([...filters, fetchingInfo.Filter]);
                else
                    currentFetchingInfo.Filter = new AndFilter(filters);
                await this.execute({ source: 'search' }, currentFetchingInfo, displayColumns, [], true);
            }
            else if (this.state.dblClicked) {
                await this.execute({ source: 'search' }, fetchingInfo, displayColumns, groupingColumns);
            }
        }
    }
    onTableChange = async (pagination: any, sorter: any) => {
        if (!(sorter instanceof Array) && sorter.field) {
            if (!sorter.column)
                this.ordering = undefined;
            else
                this.ordering = {
                    Property: sorter.field.toString(),
                    OrderBy: sorter.order === 'ascend' ? enmOrderingType.Ascending : enmOrderingType.Descending
                };
        }
        if (this.paging) {
            this.paging.PageNo = pagination.current ?? 1;
            this.paging.PageSize = pagination.pageSize ?? 10;
        }
        if (this.state.currentExecuteData) {
            const { fetchingInfo, groupingColumns, displayColumns } = this.state.currentExecuteData;
            const filters: ComparisonFilter[] = [];
            let currentFetchingInfo: IFetchingInfo = {
                Paging: { PageNo: this.paging!.PageNo, PageSize: this.paging!.PageSize },
                Orderings: this.ordering ? [this.ordering] : []
            };
            groupingColumns.forEach(f => {
                filters.push(new ComparisonFilter(f, enmConditionalOperators.Equal, this.currentRecord[f]));
            });
            if (filters.length) {
                if (fetchingInfo.Filter)
                    currentFetchingInfo.Filter = new AndFilter([...filters, fetchingInfo.Filter]);
                else
                    currentFetchingInfo.Filter = new AndFilter(filters);
            }
            await this.execute({ source: 'search' }, currentFetchingInfo, displayColumns, [], this.state.dblClicked);
        }
        else
            this.submit({ source: 'search' });
    }
    onGroupingColumnsSelect = (value: any) => {
        let displayColumns = this.state.displayColumns.map(c => c);
        const col = this.displayColumnsData.find(c => c.name === (value as string).split('|')[0]);
        if (col && !displayColumns.find(c => c === col?.key)) {
            displayColumns.push(col.key);
        }

        this.setState({ groupingColumns: [...this.state.groupingColumns, value as string], displayColumns });
    }
    onGroupingColumnsDeselect = (value: any) => {
        let values = this.state.groupingColumns;
        let index = values.indexOf(value as string);
        if (index > -1)
            values.splice(index, 1);
        this.setState({ groupingColumns: values });
    }
    aggregateTitleRender = (record: IDamageCaseReportColumn) => {
        const disabled = !this.state.groupingColumns.length || !this.state.displayColumns.filter(c => c === record.key).length || !!this.state.groupingColumns.find(c => c.split('|')[0] === record.name);
        const currentTitles: { value?: string; title: string; }[] = []
        if (!disabled)
            this.aggregateTitles.forEach(item => {
                let newItem = { value: item.value, title: item.title };
                if (newItem.value && newItem.value.indexOf('{X}') > -1) {
                    newItem.value = newItem.value.replace('{X}', record.name);
                    newItem.title += ' ' + record.title;
                }
                currentTitles.push({ value: newItem.value, title: newItem.title });
            });
        else {
            record.aggregateFunc = undefined;
            record.aggregateTitle = undefined;
        }
        return (
            <Select disabled={disabled} style={{ width: '100%', border: (record as any).hasWarning ? '1px solid red' : '' }} value={record.aggregateFunc} onChange={value => {
                let currentItem = currentTitles.find(i => i.value === value);
                record.aggregateFunc = currentItem?.value;
                record.aggregateTitle = currentItem?.title
            }}>
                {currentTitles.map(a => <Option key={a.value ?? '-1'} value={a.value}>{a.title}</Option>)}
            </Select>
        );

    }
    onDisplayColumnDragEnd = (event: any) => {
        event.preventDefault();

        if (!this.dragColumn)
            return;

        const fromIndex = this.state.displayColumns.indexOf(event.currentTarget.dataset.rowKey);
        const toIndex = this.state.displayColumns.indexOf(this.dragColumn);
        this.dragColumn = undefined;

        if (event.dataTransfer.dropEffect === "none")
            return;

        let displayColumns: string[] = [];
        if (fromIndex > -1 && toIndex > -1) {
            this.state.displayColumns.forEach((c, i) => {
                if (i === fromIndex)
                    return;
                if (fromIndex < toIndex) {
                    displayColumns.push(c);
                    if (i === toIndex)
                        displayColumns.push(event.currentTarget.dataset.rowKey);
                }
                else {
                    if (i === toIndex)
                        displayColumns.push(event.currentTarget.dataset.rowKey);
                    displayColumns.push(c);
                }
            });
            this.setState({ displayColumns });
        }
    }
    onDisplayColumnDragOver = (event: any) => {
        this.dragColumn = event.currentTarget.dataset.rowKey;
        event.preventDefault();
    }
    virtualRender() {
        const displayColumnsData: IDamageCaseReportColumn[] = [];
        this.state.displayColumns.forEach(key => {
            let item = this.displayColumnsData.find(i => i.key === key);
            if (item)
                displayColumnsData.push(item);
        });
        const others = this.displayColumnsData.filter(i => !this.state.displayColumns.find(k => k === i.key));
        displayColumnsData.push(...others);

        const loading = this.state.loading ? { indicator: <CustomSpinner /> } : false;

        const { PageNo, PageSize } = this.paging!;

        const header = this.getPageHeader({
            title: this.title,
            onBack: () => {
                if (this.props.onClose)
                    this.props.onClose(this);
                else
                    window.history.back();
            }
        });

        const components = {
            header: {
                cell: ResizableTitle,
            },
        };

        const columns = this.state.resultColumns.map((col, index) => ({
            ...col,
            onHeaderCell: (column: any) => ({
                width: column.width,
                onResize: this.handleResize(index),
            }),
        }));

        let extraStyles = this.state.access.length ? {} : { display: 'none' };

        //const defaultFilter = new NotFilter(new OrFilter([new ComparisonFilter("DamageCaseTypeId", enmConditionalOperators.NotEqual, "1")]));
        const defaultFilter = this.state.defaultFilter;

        return (
            <div>
                {header}

                <div className="box" >
                    <Tabs defaultActiveKey="1" style={{ background: "rgb(137 167 235 / 20%)", position: "sticky", top: 120 }}>
                        <TabPane
                            tab={
                                <div style={{ marginRight: 4 }}>
                                    <FilterOutlined style={{ verticalAlign: "middle" }} />
                                    <span>
                                        فیلتر
                                    </span>
                                </div>
                            }
                            key="1"
                            style={{ padding: "0px 10px 10px 10px" }}
                        >
                            <AdvancedSearch
                                columns={this.reportColumns}
                                onSubmit={this.onSubmit}
                                ref={node => this._advancedSearch = node}
                                defaultFilter={defaultFilter}
                                locked={!!defaultFilter && this.state.access.indexOf('Save') === -1}
                                defaultSearchParams={this.state.defaultSearchParams}
                            />
                        </TabPane>
                        <TabPane
                            tab={
                                <div style={{ ...extraStyles }}>
                                    <SettingOutlined style={{ verticalAlign: "middle" }} />
                                    <span>
                                        تنظیمات
                                    </span>
                                </div>
                            }
                            key="2"
                            style={{ padding: 10, ...extraStyles }}
                        >
                            <div>
                                <Space style={{ width: "100%" }} direction="vertical">
                                    <Row>
                                        <label style={{ margin: 5, minWidth: 200 }}>عنوان الگو</label>
                                        <Input style={{ width: 300 }}
                                            defaultValue={this.state.templateTitle ?? ""}
                                            onChange={(e: React.ChangeEvent<any>) => {
                                                this.setState({ templateTitle: e.target.value });
                                            }}
                                        />
                                    </Row>
                                    <Row>
                                        <label style={{ margin: 5, minWidth: 200 }}>ستونهای گروهبندی</label>
                                        <Select
                                            mode="multiple"
                                            value={this.state.groupingColumns}
                                            style={{ minWidth: 300 }}
                                            showArrow
                                            allowClear
                                            onSelect={this.onGroupingColumnsSelect}
                                            onDeselect={this.onGroupingColumnsDeselect}
                                        >
                                            {this.reportColumns.filter(c => c.isGroupable === true).map(c => <Option key={c.name} value={c.name + "|" + c.title}>{c.title}</Option>)}
                                        </Select>
                                    </Row>
                                    <Row>
                                        <label style={{ margin: 5, minWidth: 200 }}>نمایش ستون تعداد</label>
                                        <Checkbox
                                            checked={this.state.showCountColumn && !!this.state.groupingColumns.length}
                                            disabled={!this.state.groupingColumns.length}
                                            onChange={(e: CheckboxChangeEvent) => this.setState({ showCountColumn: e.target.checked })} />
                                    </Row>
                                    <Row>
                                        <label style={{ margin: 10, minWidth: 200 }}>ستونهای نمایشی</label>
                                        <Table
                                            rowKey="key"
                                            dataSource={displayColumnsData}
                                            rowSelection={{
                                                type: "checkbox",
                                                columnWidth: "50px",
                                                selectedRowKeys: this.state.displayColumns,
                                                onChange: this.onSelectedDisplayColumnsChanged
                                            }}
                                            onRow={() => {
                                                return {
                                                    draggable: "true",
                                                    onDragEnd: (e) => { this.onDisplayColumnDragEnd.call(this, e); },
                                                    onDragOver: (e) => { this.onDisplayColumnDragOver.call(this, e); }
                                                };
                                            }}
                                            pagination={{ defaultPageSize: 100 }}
                                            columns={[
                                                {
                                                    key: "title",
                                                    title: "عنوان ستون",
                                                    dataIndex: "title",
                                                    width: 300
                                                },
                                                {
                                                    key: "aggregateTitle",
                                                    title: "عنوان تجمیع",
                                                    dataIndex: "aggregateTitle",
                                                    width: 300,
                                                    render: (_: any, record: IDamageCaseReportColumn) => this.aggregateTitleRender(record)
                                                }
                                            ]}
                                        />
                                    </Row>
                                </Space>
                            </div>
                            <div>
                            </div>
                        </TabPane>
                    </Tabs>
                </div>

                <Space style={{ padding: 10, display: "flex", direction: "ltr", position: "sticky", bottom: -10, background: "rgba(250, 250, 250, 0.85)" }} >
                    <Button onClick={this.search} type="primary" icon={<SearchOutlined style={{ verticalAlign: "middle" }} />} loading={this.state.loading}>جستجو</Button>
                    <Button onClick={this.excel} icon={<FileExcelOutlined style={{ verticalAlign: "middle" }} />} loading={this.state.loading}>اکسل</Button>
                    <Button
                        onClick={this.setDefaults}
                        icon={<SettingOutlined style={{ verticalAlign: "middle" }} />}
                        loading={this.state.loading}
                    >
                        تنظیمات پیشفرض
                    </Button>
                    <Button
                        style={{ display: this.state.access.indexOf("Save") > -1 ? 'block' : 'none' }}
                        onClick={this.saveTemplate}
                        icon={<SaveOutlined style={{ verticalAlign: "middle" }} />}
                        loading={this.state.loading}
                        type="primary"

                    >
                        ذخیره الگو
                    </Button>
                    <Popconfirm title="آیا مایلید الگو حذف شود؟" onConfirm={this.deleteTemplate}>
                        <Button
                            style={{ display: this.state.access.indexOf("Delete") > -1 && !!this.state.currentTemplate ? 'block' : 'none' }}
                            type="primary"
                            danger
                            icon={<DeleteOutlined style={{ verticalAlign: "middle" }} />}
                            loading={this.state.loading}
                        >
                            حذف الگو
                        </Button>
                    </Popconfirm>
                </Space>

                {
                    this.state.data
                        ?
                        <div className="box" style={{ marginTop: 10 }} ref={node => this._gridContainer = node} >
                            <Table
                                components={components}
                                rowKey={(record: any) => {
                                    record.RowKey = Core.newId();
                                    return record.RowKey;
                                }}
                                dataSource={this.state.data}
                                loading={loading}
                                columns={columns}
                                scroll={{ x: 'max-content' }}
                                sticky={{ getContainer: () => { return this._gridContainer!; } }}
                                pagination={{
                                    style: { marginRight: 10 },
                                    position: ['bottomRight'],
                                    defaultPageSize: this.paging?.PageSize ?? 10,
                                    current: Math.ceil(this.state.totalResultCount ?? 0 / PageSize) < PageNo ? 1 : PageNo,
                                    total: this.state.totalResultCount,
                                    showSizeChanger: true,
                                    showTotal: (total, _) => {
                                        return (<div>تعداد کل ردیف ها: {total} </div>);
                                    }
                                }}
                                onRow={(record: any) => {
                                    const { groupingColumns } = this.state.currentExecuteData!;
                                    const cursor = this.state.dblClicked || groupingColumns.length ? 'pointer' : 'default';
                                    const title = this.state.dblClicked ? "برای برگشت دبلکلیک نمایید" : groupingColumns.length ? "برای مشاهده جزئیات دبلکلیک نمایید" : undefined;
                                    return {
                                        title,
                                        style: { cursor },
                                        onDoubleClick: () => this.onRowDoubleClick(record),
                                    }
                                }}
                                onChange={(p, _, s) => this.onTableChange(p, s)}
                                ref={node => {
                                    if (node && loading !== false)
                                        node.scrollIntoView();
                                }}
                            />
                        </div>
                        :
                        <></>
                }

            </div>
        );
    }
}